/**
 * v-showwhitespace
 *
 * Makes leading/trailing whitespace of an input visible by painting a colored
 * band exactly under those characters. CSS alone cannot target a substring of
 * an input's value, so the width of the whitespace run is measured with a
 * canvas using the input's own font and drawn as a background gradient.
 *
 * Usage:
 *   <InputText v-showwhitespace />                      // leading + trailing
 *   <InputText v-showwhitespace="{ trailing: false }" /> // leading only
 *   <InputText v-showwhitespace="false" />               // disabled
 *
 * The input also gets a `data-whitespace` attribute ("leading", "trailing" or
 * "leading trailing") so extra styling can be attached from CSS.
 */

const HIGHLIGHT_COLOR = 'rgba(255, 97, 1, 0.35)';
const STATE = Symbol('showwhitespace');

let measureCtx = null;

const getMeasureCtx = () => {
    if (!measureCtx) {
        measureCtx = document.createElement('canvas').getContext('2d');
    }
    return measureCtx;
};

const getInput = (el) => {
    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        return el;
    }
    return el.querySelector('input, textarea');
};

const getOptions = (value) => {
    const opts = value && typeof value === 'object' ? value : {};
    return {
        leading: opts.leading !== false,
        trailing: opts.trailing !== false,
        color: opts.color || HIGHLIGHT_COLOR,
    };
};

const clear = (input) => {
    input.style.backgroundImage = '';
    input.removeAttribute('data-whitespace');
};

const paint = (input, options) => {
    const value = input.value || '';

    if (!value) {
        clear(input);
        return;
    }

    // a value made only of spaces counts as leading whitespace
    const leadingCount = value.length - value.trimStart().length;
    const trailingCount = value.trim() === '' ? 0 : value.length - value.trimEnd().length;

    const kinds = [];
    if (leadingCount && options.leading) kinds.push('leading');
    if (trailingCount && options.trailing) kinds.push('trailing');

    if (!kinds.length) {
        clear(input);
        return;
    }

    const style = getComputedStyle(input);
    const ctx = getMeasureCtx();
    ctx.font = [
        style.fontStyle,
        style.fontVariant,
        style.fontWeight,
        style.fontSize,
        style.fontFamily,
    ].join(' ');
    if ('letterSpacing' in ctx) {
        ctx.letterSpacing = style.letterSpacing === 'normal' ? '0px' : style.letterSpacing;
    }
    const widthOf = (text) => ctx.measureText(text).width;

    // x = 0 is the left edge of the padding box (background-origin: padding-box)
    const paddingLeft = parseFloat(style.paddingLeft) || 0;
    const paddingRight = parseFloat(style.paddingRight) || 0;
    const textOrigin = paddingLeft - input.scrollLeft;
    const min = paddingLeft;
    const max = Math.max(min, input.clientWidth + paddingLeft - paddingRight);
    const clamp = (x) => Math.min(Math.max(x, min), max);

    const bands = [];
    if (kinds.includes('leading')) {
        bands.push([textOrigin, textOrigin + widthOf(value.slice(0, leadingCount))]);
    }
    if (kinds.includes('trailing')) {
        const beforeTrailing = textOrigin + widthOf(value.slice(0, value.length - trailingCount));
        bands.push([beforeTrailing, textOrigin + widthOf(value)]);
    }

    const stops = [];
    let cursor = 0;
    bands.forEach(([rawStart, rawEnd]) => {
        const start = clamp(rawStart);
        const end = clamp(rawEnd);
        if (end - start < 0.5) {
            return;
        }
        stops.push(`transparent ${cursor}px`, `transparent ${start}px`);
        stops.push(`${options.color} ${start}px`, `${options.color} ${end}px`);
        cursor = end;
    });

    if (!stops.length) {
        clear(input);
        return;
    }
    stops.push(`transparent ${cursor}px`);

    input.style.backgroundImage = `linear-gradient(to right, ${stops.join(', ')})`;
    input.setAttribute('data-whitespace', kinds.join(' '));
};

const attach = (el, binding) => {
    const input = getInput(el);
    if (!input) {
        return;
    }

    const options = getOptions(binding.value);
    const enabled = binding.value !== false && binding.value !== null && binding.value !== undefined;

    if (!enabled) {
        detach(el);
        return;
    }

    let state = el[STATE];
    if (state) {
        state.options = options;
    } else {
        const update = () => paint(input, el[STATE].options);
        state = { input, options, update };
        el[STATE] = state;

        ['input', 'change', 'focus', 'blur', 'scroll'].forEach((evt) =>
            input.addEventListener(evt, update)
        );
        // fonts and layout may not be settled on mount
        requestAnimationFrame(update);
    }

    state.update();
};

const detach = (el) => {
    const state = el[STATE];
    if (!state) {
        return;
    }
    ['input', 'change', 'focus', 'blur', 'scroll'].forEach((evt) =>
        state.input.removeEventListener(evt, state.update)
    );
    clear(state.input);
    delete el[STATE];
};

export default {
    mounted: attach,
    updated: attach,
    unmounted: detach,
};
